import type { ResponseUser } from '../types'
import { responseRecovery, RESPONSE_CONTRACT_MESSAGE, type createResponseRecovery } from '../lib/response-recovery'
import { ResponseContractError, type ResponseRequestContext } from './response-contract-error'
import { validatePageResponse } from './validate-response'

type Recovery = ReturnType<typeof createResponseRecovery>

export type BootstrapPage = Record<string, unknown> & {
  component: string
  props: Record<string, unknown>
  url: string
  version: string | null
}

const bootstrapContext: ResponseRequestContext = {
  id: 'bootstrap',
  semantics: 'page-request',
  action: 'explicit',
}

export function validateInitialPage(
  page: unknown,
  {
    fallbackCsrf,
    recovery = responseRecovery,
    report = () => {},
  }: {
    fallbackCsrf: string
    recovery?: Recovery
    report?: (error: ResponseContractError, context: ResponseRequestContext, user: ResponseUser | null) => void
  },
): { page: BootstrapPage; user: ResponseUser | null } {
  try {
    const result = validatePageResponse(page)
    const user = result.page.props.user as ResponseUser | null
    if (result.contractError) {
      recovery.recordFlashFailures(result.flashFailures, true)
      report(result.contractError, {
        ...bootstrapContext,
        component: result.page.component,
        url: result.page.url as string,
      }, user)
    } else {
      recovery.clearAfterValidResponse()
    }
    return { page: result.page as BootstrapPage, user }
  } catch (error) {
    if (!(error instanceof ResponseContractError)) throw error
    report(error, bootstrapContext, null)

    return {
      page: {
        component: 'Error',
        props: {
          csrf: fallbackCsrf,
          errors: {},
          user: null,
          message: RESPONSE_CONTRACT_MESSAGE,
        },
        flash: {},
        url: typeof location === 'undefined' ? '/' : `${location.pathname}${location.search}`,
        version: null,
        encryptHistory: true,
        clearHistory: false,
        rescuedProps: [],
        rememberedState: {},
      },
      user: null,
    }
  }
}

export function parseInitialPage(
  serializedPage: string,
  dependencies: Parameters<typeof validateInitialPage>[1],
): ReturnType<typeof validateInitialPage> {
  let page: unknown
  try {
    page = JSON.parse(serializedPage)
  } catch {
    page = undefined
  }
  return validateInitialPage(page, dependencies)
}
